/**
 * @file src/components/organisms/RiskReturnSplitBarDial/RiskReturnSplitBarDial.tsx
 * @description This file defines the RiskReturnSplitBarDial component that displays risk and return scores using a split bar dial.
 * @module RiskReturnSplitBarDial
 */

import React, { FC } from 'react';
import { ListItem } from '@/components';
import SplitBarDial from '@/components/atoms/BarDial/SplitBarDial';
import { YieldBearingAssetInterface } from '@/interfaces';

interface RiskReturnSplitBarDialProps {
  asset: YieldBearingAssetInterface;
}

const getRiskTypeColor = (
  score: number | undefined,
):
  | 'error'
  | 'primary'
  | 'accent'
  | 'secondary'
  | 'warning'
  | 'disabled'
  | null
  | undefined => {
  switch (score) {
    case 0:
    case 1:
      return 'accent';
    case 2:
      return 'primary';
    case 3:
    case 4:
      return 'error';
    default:
      return null;
  }
};

const getReturnTypeColor = (
  score: number | undefined,
):
  | 'error'
  | 'primary'
  | 'accent'
  | 'secondary'
  | 'warning'
  | 'disabled'
  | null
  | undefined => {
  switch (score) {
    case 0:
    case 1:
      return 'error';
    case 2:
      return 'primary';
    case 3:
    case 4:
      return 'accent';
    default:
      return null;
  }
};

const RiskReturnSplitBarDial: FC<RiskReturnSplitBarDialProps> = ({ asset }) => {
  const riskTypeColor = getRiskTypeColor(asset.riskScore);
  const returnTypeColor = getReturnTypeColor(asset.returnScore);

  return (
    <SplitBarDial
      width="w-24"
      height="h-4"
      bottomValue={asset.riskScore ? (asset.riskScore / 4) * 100 : 0}
      topValue={asset.returnScore ? (asset.returnScore / 4) * 100 : 0}
      hoverContent={
        <div className="flex flex-col items-start gap-sm">
          <ListItem
            mainText={asset.returnScore ? `${asset.returnType}` : 'Unknown'}
            mainTextColor={returnTypeColor}
            subSubText={'Return'}
          />
          <ListItem
            mainText={asset.riskType ? `${asset.riskType}` : 'Unknown'}
            mainTextColor={riskTypeColor}
            subSubText={'Risk'}
          />
        </div>
      }
    />
  );
};

export default RiskReturnSplitBarDial;
